import { FC, useState, useEffect } from "react";
import {
  Container,
  Paper,
  Box,
  styled,
} from "@mui/material";
import { makeStyles } from '@material-ui/core/styles';
import { CircularProgressMolecule } from "../components/molecules/CircularProgressMolecule";
import { VisualizedMenus } from "../components/VisualizedMenus";
import { getCredit, useAuth0Token } from "../common/http-requests";
import {
  creditRequestType,
  creditPs8ResponseType,
  creditResponseType
} from "../common/type";
import { FeedbackBar } from "../components/molecules/FeedbackBar";
import { Toolbar } from "../components/FooterToolbar";
import { useCustomMediaQuery } from "../common/useCustomMediaQuery";
import CreditRadarChart from '../components/CreditRadarChart';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';//上矢印
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';//下矢印
import Groups3OutlinedIcon from '@mui/icons-material/Groups3Outlined';  // 教職コンピテンシー
import SettingsAccessibilityOutlinedIcon from '@mui/icons-material/SettingsAccessibilityOutlined';  //トランスファラブルスキル
import {
  descriptionText,
  commentCredit
} from "../common/Comment";
import CommentMessages from "../components/CommentMessages";
import { useTranslation } from 'react-i18next';
import { themeColors } from "../common/colors";
import {
  useUserId,
  useUserNickname,
  useLanguage,
} from "../hooks/useStoreGetters";

const BAR_CHART_SIZE: number[] = [
  1000, 320
];
// Material-UIのスタイル設定
const useStyles = makeStyles({
  chartContainer: {
    position: 'relative',
    marginTop: '20px',
    padding: '10px 20px 15px',
    overflow: 'hidden'
  }
});

const RADAR_CHART_SIZE: number[] = [
  800, 320
];

const AccordionLabel = styled(Box)(() => ({
  position: "relative",
  width: '100%',
  height: 'auto',
  maxWidth: `${RADAR_CHART_SIZE[0]}px`,
  margin: '8px auto 0',
  borderTop: `solid 1px ${themeColors[0]}`,
  borderBottom: `solid 1px ${themeColors[0]}`,
  color: `${themeColors[0]}`,
}));

const AccordionButton = styled(Box)(() => ({
  position: "absolute",
  display: "inline-block",
  top: "0",
  right: "0",
}));

const Accordion = styled(Box)(() => ({
  overflow: 'hidden',
  transition: 'max-height 0.3s ease-out',
}));

const DefaultStagePoint: creditPs8ResponseType = {
  ps8_credit_1: 0.0,
  ps8_credit_2: 0.0,
  ps8_credit_3: 0.0,
  ps8_credit_4: 0.0,
  ps8_credit_5: 0.0,
  ps8_credit_6: 0.0,
  ps8_credit_7: 0.0,
  ps8_credit_8: 0.0,
};

const CreditPage: FC = () => {
  const { t } = useTranslation();
  const [isSending, setIsSending] = useState(true);
  const { getToken } = useAuth0Token();
  const userId = useUserId();
  const userNickname = useUserNickname();
  const language = useLanguage();
  const classes = useStyles();
  const { isDesktop, isMobile } = useCustomMediaQuery();

  const [isSEOpen, setIsSEOpen] = useState(true);
  const [seHeight, setSEHeight] = useState('0px');
  const [isTSOpen, setIsTSOpen] = useState(true);
  const [tsHeight, setTSHeight] = useState('0px');
  const [commentMessage, setCommentMessage] = useState<string>('');


  // データ例
  //   "previous_credits":
  //     { "ps8_credit_1": 160, "ps8_credit_2": 235, "ps8_credit_3": 188, "ps8_credit_4": 79, "ps8_credit_5": 192, "ps8_credit_6": 188, "ps8_credit_7": 329, "ps8_credit_8": 105 },
  //   "latest_credits":
  //     { "ps8_credit_1": 171, "ps8_credit_2": 239, "ps8_credit_3": 195, "ps8_credit_4": 83, "ps8_credit_5": 196, "ps8_credit_6": 196, "ps8_credit_7": 332, "ps8_credit_8": 120 }

  const [creditData, setCreditData] = useState<creditResponseType>({
    previous_credits: DefaultStagePoint,
    latest_credits: DefaultStagePoint,
    base_credits: DefaultStagePoint,
    period_count: 0,
  });


  // api取得失敗時のアラート
  const [feedbackInfo, setFeedbackInfo] = useState({
    open: false,
    text: "",
    type: "success" as "error" | "success" | "warning" | "info",
    autoHideDuration: null,
  });

  const [accordionHeight] = useState("38vw"); // Mobile時のAccordionnの高さ指定　// 40px

  // clickawayした際、api取得失敗時のアラートを行わない。
  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedbackInfo({ ...feedbackInfo, open: false });
  };

  // 以下は、記録一覧画面のコードを流用した。
  // Phase1と異なり、画面遷移が必要ではないため、location（uselocation）は使用しない。
  // React18(2022年)より、strictMode（javascriptのコードを通常より厳しくエラーチェックできるモード）において
  // オフスクリーンから再度表示されることで、useEffectが２回発火（キック）される仕様変更となっため、1回に制限するよう変更した。
  // また、以下のコードの})();の();は、定義した関数を即時実行している。
  // 最後にreturn文にて定義された関数は、クリーンアップ関数であり、リスナー、オブザーバー等のオブジェクトを設定した場合、
  // レンダリング時に毎回インスタンスを作成されるため、メモリーリーク及びメモリーの圧迫を抑止するための関数。
  // useEffectの依存配列（第2引数）は、通常、空リストもしくは、具体的な変数のリストを登録するのが一般的であり、
  // 定義しない場合は、FCのstate, propsのうちいずれかが更新される度に実行されてしまうため、通常、定義しないことはない。
  let alreadyFiredOnce = false;
  useEffect(() => {
    (async () => {
      if (!alreadyFiredOnce) {
        await getCreditData(userId);
      }
    })();
    return () => {
      alreadyFiredOnce = true;
    };
  }, []);

  const getCreditData = async (
    login_user_id: number,
  ) => {
    setIsSending(true);
    try {
      const requestParams: creditRequestType = {
        login_user_id,
      };
      const data: creditResponseType = await getCredit(
        await getToken(),
        requestParams
      );
      setCreditData(data);

      const comment = commentCredit(login_user_id, userNickname, t, data);
      setCommentMessage(comment);
    } catch {
      setFeedbackInfo({
        ...feedbackInfo,
        open: true,
        text: t("msg.cannotLoadData"),
        type: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    setSEHeight(isSEOpen ? `${RADAR_CHART_SIZE[1]}px` : '0px');
  }, [isSEOpen]);

  useEffect(() => {
    setTSHeight(isTSOpen ? `${RADAR_CHART_SIZE[1]}px` : '0px');
  }, [isTSOpen]);

  const handleSEAccordion = () => {
    setIsSEOpen(!isSEOpen);
  };

  const handleTSAccordion = () => {
    setIsTSOpen(!isTSOpen);
  };

  const SummaryMessage = styled(Box)(() => ({
    maxWidth: `${BAR_CHART_SIZE[0]}px`,
    margin: isDesktop ? '3.5rem auto 1rem' : '7.5rem auto 1rem',
    textAlign: 'center',
  }));

  return (
    <Container maxWidth="xl">
      {isSending ?
        <CircularProgressMolecule displayLabel={t("msg.loading")} /> :
        (
          <>
            <VisualizedMenus />
            <FeedbackBar
              feedbackInfo={feedbackInfo}
              handleClose={handleClose}
            ></FeedbackBar>
            <SummaryMessage>{descriptionText(userNickname, language, t, "credit")}<br/>{t("common.chartNotation")}</SummaryMessage>
            <CommentMessages message={commentMessage} />
            <Paper className={classes.chartContainer}>
              <AccordionLabel>
                <Groups3OutlinedIcon sx={{ margin: "0 10px 0 5px" }} />
                {t("credit.chart_title_1")}
                <AccordionButton onClick={handleSEAccordion}>
                  {isSEOpen ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </AccordionButton>
              </AccordionLabel>
              <Accordion sx={{ height: isDesktop && isSEOpen ? seHeight : isMobile && isSEOpen ? accordionHeight : seHeight }}>
                <CreditRadarChart
                  raderChartSize={RADAR_CHART_SIZE}
                  data={creditData}
                  target="seData"
                />
              </Accordion>

              <AccordionLabel>
                <SettingsAccessibilityOutlinedIcon sx={{ margin: "0 10px 0 5px" }} />
                {t("credit.chart_title_2")}
                <AccordionButton onClick={handleTSAccordion}>
                  {isTSOpen ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </AccordionButton>
              </AccordionLabel>
              <Accordion sx={{ height: isDesktop && isTSOpen ? tsHeight : isMobile && isTSOpen ? accordionHeight : tsHeight }}>
                <CreditRadarChart
                  raderChartSize={RADAR_CHART_SIZE}
                  data={creditData}
                  target="tsData"
                />
              </Accordion>
            </Paper>
          </>
        )}
      <Toolbar />
    </Container>
  );
};

export default CreditPage;
